import React from "react";
import {
  MdCheckCircle,
  MdCancel,
  MdShoppingCart,
  MdAttachMoney,
} from "react-icons/md";
import Card from "components/card";
import {
  StripeData,
  RevenueCatSubscription,
  RevenueCatTransactionHistory,
} from "../../types/financial";

interface SubscriptionOverviewCardProps {
  hasActiveSubscription: boolean;
  stripeData?: StripeData; // Optional - may not exist if user has no Stripe account
  revenueCatSubscriptions: RevenueCatSubscription[];
  revenueCatTransactions?: RevenueCatTransactionHistory; // Optional - live history from RevenueCat
}

const SubscriptionOverviewCard: React.FC<SubscriptionOverviewCardProps> = ({
  hasActiveSubscription,
  stripeData,
  revenueCatSubscriptions,
  revenueCatTransactions,
}) => {
  // The live per-transaction history when RevenueCat answered with rows, so the tile
  // agrees with the Transaction History list below it; the stored chains otherwise.
  const liveHistory =
    revenueCatTransactions && revenueCatTransactions.transactions.length > 0
      ? revenueCatTransactions
      : null;

  const revenueCatTotal = liveHistory
    ? liveHistory.total_spent
    : revenueCatSubscriptions.reduce((sum, subscription) => {
        return sum + parseFloat(subscription.price);
      }, 0);
  const revenueCatCount = liveHistory
    ? liveHistory.total_transactions
    : revenueCatSubscriptions.length;
  const revenueCatLabel = liveHistory
    ? "RevenueCat Payments"
    : "RevenueCat Subs";

  // Safe access to stripe data with fallbacks
  const stripeTotal = stripeData?.total_spent || 0;
  const stripePurchases = stripeData?.total_purchases || 0;

  // Combined totals (Stripe + RevenueCat)
  const totalSpent = stripeTotal + revenueCatTotal;
  const totalPurchases = stripePurchases + revenueCatCount;

  return (
    <Card extra="w-full p-3 md:p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold text-navy-700 dark:text-white">
          Financial Overview
        </h2>
        <span
          className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ${
            hasActiveSubscription
              ? "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100"
              : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100"
          }`}
        >
          {hasActiveSubscription ? (
            <>
              <MdCheckCircle className="mr-1 h-4 w-4" />
              Active Subscription
            </>
          ) : (
            <>
              <MdCancel className="mr-1 h-4 w-4" />
              No Active Subscription
            </>
          )}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {/* Total Spent */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Total Spent
            </span>
            <MdAttachMoney className="h-6 w-6 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-navy-700 dark:text-white">
            ${totalSpent.toFixed(2)}
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {totalPurchases} payment{totalPurchases !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Stripe Purchases */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Stripe Purchases
            </span>
            <MdShoppingCart className="h-6 w-6 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-navy-700 dark:text-white">
            {stripePurchases}
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            ${stripeTotal.toFixed(2)} total
          </div>
        </div>

        {/* RevenueCat */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {revenueCatLabel}
            </span>
            <MdCheckCircle className="h-6 w-6 text-purple-500" />
          </div>
          <div className="text-3xl font-bold text-navy-700 dark:text-white">
            {revenueCatCount}
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            ${revenueCatTotal.toFixed(2)} total
          </div>
        </div>
      </div>
    </Card>
  );
};

export default SubscriptionOverviewCard;
